/**
 * Impersonation Banner
 *
 * Shows a prominent banner when admin is impersonating another user.
 * Includes the impersonated user's name, role, and a button to stop impersonation.
 */

import { useTranslation } from "react-i18next";
import { UserCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/shared/components/atoms/button";
import { useImpersonationStore } from "../model/impersonationStore";
import { useWorkRoleStore } from "../model/workRoleStore";
import { userScopeApi } from "@/entities/auth";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";
import { toast } from "sonner";

export function ImpersonationBanner() {
  const { t } = useTranslation(["pages"]);

  const {
    isImpersonating,
    impersonatedUserName,
    impersonatedUserRole,
    stopImpersonation,
    realAdminContext,
  } = useImpersonationStore();

  const { setCurrentContext } = useWorkRoleStore();
  const { setScope, clearScope } = useUserScopeStore();

  const handleStopImpersonation = async () => {
    if (realAdminContext) {
      setCurrentContext(realAdminContext);
    }
    stopImpersonation();

    try {
      const scope = await userScopeApi.buildUserScope();
      setScope(scope);
    } catch {
      clearScope();
    }

    toast.success(t("pages:impersonation.stopped", "Impersonation ended"), {
      description: t("pages:impersonation.backToAdmin", "You are back as Account Admin"),
    });
  };

  if (!isImpersonating()) {
    return null;
  }

  return (
    <div className="bg-warning text-warning-foreground flex h-12 w-full shrink-0 items-center px-4 shadow-md">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCheck className="h-5 w-5" />
          <span className="font-medium">
            {t("pages:impersonation.banner", "Demo: Du agierst als")}{" "}
            <strong>{impersonatedUserName}</strong>{" "}
            <span className="opacity-75">({impersonatedUserRole})</span>
          </span>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleStopImpersonation}
          className="border-warning-foreground/30 hover:bg-warning-foreground/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("pages:impersonation.stopButton", "Zurück zu Admin")}
        </Button>
      </div>
    </div>
  );
}
